import { useEffect, useState, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useSelector } from "@legendapp/state/react";
import {
  createExternalUser,
  getExternalUser,
  resendInvite,
  updateExternalUser,
} from "@/apis/clients/externalUsersClient";
import {
  buildCreateInput,
  buildPatchInput,
  type UserFormValues,
} from "@/pages/admin/UserManagementPage/buildUserInput";
import { dropdownPillPt } from "@/pages/admin/UserManagementPage/dropdownPill.pt";
import type { ExternalUser, UserRole } from "@/types/externalUser";
import {
  EMAIL_PATTERN,
  USER_EMAIL_MAX_LENGTH,
  USER_FIRST_NAME_MAX_LENGTH,
  USER_LAST_NAME_MAX_LENGTH,
} from "@/utils/validation";
import { AgenciesStore$ } from "@/stores/agenciesStore";
import { toErrorMessage } from "@/stores/storeHelpers";
import { TEXT } from "@/constants/text";
import styles from "@/pages/admin/UserManagementPage/UserFormModal.module.css";

// The role every user created here is given in T1.
//
// REPORTER sends roles [6, 4, 15], which is the set the design's risk
// acceptance was measured against: type AGENCY, role REPORTER, scope SELECT,
// every edit and view flag false except view_dmp_reports, and read on one
// agency. Raising it changes what an external user can do upstream, and role
// cannot be changed after creation — the API refuses it — so this value is the
// only chance to get it right.
export const CREATED_ROLE: UserRole = "REPORTER";

const ROLE_OPTIONS: { label: string; value: UserRole }[] = [
  { label: TEXT.userManagement.roleAdmin, value: "ADMIN" },
  { label: TEXT.userManagement.roleManager, value: "MANAGER" },
  { label: TEXT.userManagement.roleReporter, value: "REPORTER" },
];

// Agencies come from the API through AgenciesStore$, which AppLayout has
// already populated for the configured organization by the time this renders.
// The value is the agency id; the label is its name. Binding to the id means a
// rename upstream does not strand a user on a name that no longer exists.

interface UserFormModalProps {
  mode: "add" | "edit";
  user?: ExternalUser;
  onSaved: () => void;
  onCancel: () => void;
}

const UserFormModal: FC<UserFormModalProps> = ({
  mode,
  user,
  onSaved,
  onCancel,
}) => {
  const agencies = useSelector(() => AgenciesStore$.agencies.get());
  const agencyDropdownOptions = agencies.map((a) => ({
    label: a.name,
    value: a.id,
  }));

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // The row this modal was opened from carries no agency: the list cannot
  // resolve one in bulk. So an edit fetches the user it is editing, and until
  // that lands the form does not know what agency the user holds.
  //
  // Saving before it lands would be the bug this fetch exists to fix —
  // buildPatchInput would compare the picker against a missing value, decide
  // the agency changed, and rewrite the grant and revoke the user's sessions.
  // The submit button stays disabled until `loadedUser` is set.
  const [loadedUser, setLoadedUser] = useState<ExternalUser | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UserFormValues>({
    defaultValues: {
      agencyId: user?.agencyId ?? undefined,
      email: user?.email ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      // Not "??": role is a hidden field with no valid "unknown" state of its
      // own, and this value is never read back out — buildPatchInput never
      // sends role, since the API refuses to change it after creation. The
      // read-only label below is what actually needs to distinguish "" from
      // a real role, and does so separately.
      role: user?.role || CREATED_ROLE,
    },
  });

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    getExternalUser(user.id)
      .then((fresh) => {
        if (cancelled) return;
        setLoadedUser(fresh);
        // Only the agency: everything else was already correct on the row the
        // administrator clicked, and overwriting the fields they may have
        // started typing into would be worse than leaving them.
        //
        // Left unset when the user holds none upstream, so the picker's own
        // "required" rule makes them choose rather than us inventing one.
        if (fresh.agencyId !== null) setValue("agencyId", fresh.agencyId);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user, setValue]);

  // A failed lookup must not read as "this user has no agency" — that is the
  // one wrong conclusion an empty picker invites. Saying so is better than an
  // empty dropdown that looks like an answer.
  const notice = loadFailed ? TEXT.userManagement.agencyLookupFailed : message;

  const onSubmit = async (values: UserFormValues) => {
    setSubmitting(true);
    setMessage(null);
    setSuccessMessage(null);
    try {
      if (user) {
        const patch = buildPatchInput(values, loadedUser ?? user);
        // Nothing edited: an empty PATCH is refused by the API as "nothing to
        // change", and there is no reason to ask.
        if (Object.keys(patch).length > 0) {
          await updateExternalUser(user.id, patch);
        }
        onSaved();
        return;
      }
      const { inviteSent } = await createExternalUser(buildCreateInput(values));
      if (!inviteSent) {
        // The account exists. Only the email failed, and it can be resent.
        setMessage(TEXT.userManagement.inviteNotSent);
      }
      onSaved();
    } catch (error) {
      setMessage(toErrorMessage(error) || TEXT.userManagement.saveFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      draggable={false}
      header={
        <div className={styles.header}>
          <h2 className={styles.title}>
            {mode === "edit"
              ? TEXT.userManagement.modalEditTitle
              : TEXT.userManagement.modalAddTitle}
          </h2>
          {mode === "edit" && user && (
            <span className={styles.subtitle}>
              {user.firstName} {user.lastName} &middot; {user.email}
            </span>
          )}
        </div>
      }
      modal
      pt={{ root: { className: styles.dialog } }}
      resizable={false}
      visible
      onHide={onCancel}
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {notice && (
          <span className={styles.error} role="alert">
            {notice}
          </span>
        )}
        {successMessage && (
          <span className={styles.success} role="status">
            {successMessage}
          </span>
        )}
        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="user-form-first-name">
              {TEXT.userManagement.firstNameLabel}
            </label>
            <input
              className={styles.input}
              id="user-form-first-name"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register("firstName", {
                maxLength: {
                  message: TEXT.userManagement.firstNameTooLong,
                  value: USER_FIRST_NAME_MAX_LENGTH,
                },
                required: TEXT.userManagement.firstNameRequired,
              })}
            />
            {errors.firstName && (
              <span className={styles.error} role="alert">
                {errors.firstName.message}
              </span>
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="user-form-last-name">
              {TEXT.userManagement.lastNameLabel}
            </label>
            <input
              className={styles.input}
              id="user-form-last-name"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register("lastName", {
                maxLength: {
                  message: TEXT.userManagement.lastNameTooLong,
                  value: USER_LAST_NAME_MAX_LENGTH,
                },
                required: TEXT.userManagement.lastNameRequired,
              })}
            />
            {errors.lastName && (
              <span className={styles.error} role="alert">
                {errors.lastName.message}
              </span>
            )}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="user-form-email">
            {TEXT.userManagement.emailLabel}
          </label>
          {mode === "edit" && user ? (
            <span className={styles.readOnlyValue}>
              <i aria-hidden="true" className="pi pi-lock" />
              {user.email}
            </span>
          ) : (
            <>
              <input
                className={styles.input}
                id="user-form-email"
                type="email"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register("email", {
                  maxLength: {
                    message: TEXT.userManagement.emailInvalid,
                    value: USER_EMAIL_MAX_LENGTH,
                  },
                  pattern: {
                    message: TEXT.userManagement.emailInvalid,
                    value: EMAIL_PATTERN,
                  },
                  required: TEXT.userManagement.emailRequired,
                })}
              />
              {errors.email && (
                <span className={styles.error} role="alert">
                  {errors.email.message}
                </span>
              )}
            </>
          )}
        </div>

        <div className={styles.field}>
          <span className={styles.label}>{TEXT.userManagement.columnRole}</span>
          {/*
            There is no picker: every user this screen creates gets
            CREATED_ROLE. That is deliberate rather than unfinished — which
            roles a new external user receives is a permissions decision, not a
            UI one, and changing it changes what every account this screen
            creates can do upstream.
          */}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <input type="hidden" {...register("role")} />
          <span className={styles.readOnlyValue}>
            <i aria-hidden="true" className="pi pi-lock" />
            {
              ROLE_OPTIONS.find((o) => o.value === (user?.role ?? CREATED_ROLE))
                ?.label
            }
          </span>
        </div>

        <div className={styles.field}>
          <span className={styles.label} id="user-form-agency-label">
            {TEXT.userManagement.columnAgency}
          </span>
          <Controller
            control={control}
            name="agencyId"
            render={({ field }) => (
              <Dropdown
                ariaLabelledBy="user-form-agency-label"
                filter
                filterPlaceholder={TEXT.userManagement.agencySearchPlaceholder}
                options={agencyDropdownOptions}
                placeholder={TEXT.userManagement.agencyRequired}
                pt={dropdownPillPt}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
            )}
            rules={{ required: TEXT.userManagement.agencyRequired }}
          />
          {errors.agencyId && (
            <span className={styles.error} role="alert">
              {errors.agencyId.message}
            </span>
          )}
        </div>

        <div className={styles.footer}>
          {mode === "edit" && user?.status === "Invited" && (
            <Button
              disabled={submitting || user?.t1UserId === null}
              label={TEXT.userManagement.resendInviteButton}
              outlined
              pt={{ root: { className: styles.resendButton } }}
              type="button"
              onClick={async () => {
                setSubmitting(true);
                setMessage(null);
                setSuccessMessage(null);
                try {
                  const { inviteSent } = await resendInvite(user!.id);
                  if (inviteSent) {
                    setSuccessMessage(TEXT.userManagement.inviteResent);
                  } else {
                    setMessage(TEXT.userManagement.inviteNotSent);
                  }
                } catch (error) {
                  setMessage(
                    toErrorMessage(error) || TEXT.userManagement.saveFailed
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          )}
          <div className={styles.footerRight}>
            <Button
              label={TEXT.userManagement.cancelButton}
              outlined
              pt={{ root: { className: styles.cancelButton } }}
              type="button"
              onClick={onCancel}
            />
            <Button
              disabled={
                submitting || (user !== undefined && loadedUser === null)
              }
              label={
                mode === "edit"
                  ? TEXT.userManagement.saveChangesButton
                  : TEXT.userManagement.sendInviteButton
              }
              pt={{ root: { className: styles.saveButton } }}
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default UserFormModal;
